import './ManageQuizzes.css';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { fetchQuizzes } from '../../services/quizService';
import Pagination from '../Pagination/Pagination';

const QUIZZES_PER_PAGE = 12;

function ManageQuizzes() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadQuizzes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchQuizzes(currentPage, QUIZZES_PER_PAGE);
            if (data.content) {
                setQuizzes(data.content);
                setTotalElements(data.page?.totalElements || data.content.length);
                setTotalPages(data.page?.totalPages || Math.ceil((data.page?.totalElements || data.content.length) / QUIZZES_PER_PAGE));
            } else if (Array.isArray(data)) {
                setQuizzes(data);
                setTotalElements(data.length);
                setTotalPages(1);
            }
        } catch (err) {
            console.error('Error loading quizzes:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => {
        loadQuizzes();
    }, [loadQuizzes]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleDeleteClick = (quiz) => {
        setDeleteConfirm(quiz);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;
        try {
            setDeleting(true);
            // TODO: call deleteQuiz API when available
            // await deleteQuiz(deleteConfirm.id);
            setDeleteConfirm(null);
            loadQuizzes();
        } catch (err) {
            console.error('Error deleting quiz:', err);
            alert('Nepodařilo se smazat kvíz: ' + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    // Client-side text search
    const filteredQuizzes = searchTerm.trim()
        ? quizzes.filter(quiz => {
            const searchLower = searchTerm.toLowerCase();
            return (
                quiz.title?.toLowerCase().includes(searchLower) ||
                quiz.shortDescription?.toLowerCase().includes(searchLower)
            );
        })
        : quizzes;

    return (
        <div className="manage-quizzes-page">
            <div className="manage-quizzes-container">
                <button className="back-button" onClick={() => navigate('/editor-kvizu')}>
                    ← Zpět
                </button>
                <h1 className="page-title">Spravovat kvízy</h1>
                <p className="page-subtitle">Upravte nebo smažte existující kvízy</p>

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat kvízy podle názvu nebo popisu..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {loading ? (
                    <div className="loading-message">
                        <p>Načítání kvízů...</p>
                    </div>
                ) : (
                    <div className="quizzes-list">
                        {filteredQuizzes.length > 0 ? (
                            filteredQuizzes.map(quiz => (
                                <div key={quiz.id} className="quiz-card">
                                    <div className="quiz-card-content">
                                        <h3 className="quiz-card-title">{quiz.title}</h3>
                                        {quiz.shortDescription && (
                                            <p className="quiz-card-description">{quiz.shortDescription}</p>
                                        )}
                                        {quiz.questionCount !== undefined && (
                                            <span className="quiz-card-count">
                                                {quiz.questionCount} otázek
                                            </span>
                                        )}
                                    </div>
                                    <div className="quiz-card-actions">
                                        <button
                                            className="btn-edit"
                                            onClick={() => navigate(`/editor-kvizu/edit/${quiz.id}`)}
                                        >
                                            Upravit
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => handleDeleteClick(quiz)}
                                        >
                                            Smazat
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-results">
                                <p>{searchTerm.trim() ? 'Nebyly nalezeny žádné kvízy odpovídající vašemu hledání.' : 'Zatím nejsou žádné kvízy.'}</p>
                            </div>
                        )}
                    </div>
                )}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    itemLabel="kvízů"
                />

                {deleteConfirm && (
                    <div className="delete-modal-overlay">
                        <div className="delete-modal">
                            <h3>Smazat kvíz</h3>
                            <p>Opravdu chcete smazat kvíz <strong>"{deleteConfirm.title}"</strong>?</p>
                            <div className="delete-modal-actions">
                                <button
                                    className="btn-delete-confirm"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Mažu...' : 'Smazat'}
                                </button>
                                <button
                                    className="btn-delete-cancel"
                                    onClick={handleDeleteCancel}
                                    disabled={deleting}
                                >
                                    Zrušit
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManageQuizzes;
